import { useState } from 'react';
import { HomepageImageForm } from './HomepageImageForm';
import EditHomepageImageModal from './EditHomepageImageModal';
import AlertDialog from '../AlertDialog';
import { Trash2, Pencil } from 'lucide-react';

interface HomepageImage {
  id: string;
  image_url: string;
  top_pos: string | null;
  left_pos: string | null;
  right_pos: string | null;
  bottom_pos: string | null;
  rotation: string;
  width_val: string;
  height_val: string;
  z_index_val: number;
}

interface HomepageImageManagementProps {
  images: HomepageImage[];
  isLoading: boolean;
  error: Error | null;
  onAddImage: (file: File, imageData: any) => Promise<void>;
  onUpdateImage: (id: string, data: Partial<HomepageImage>) => Promise<void>;
  onDeleteImage: (id: string, imageUrl: string) => Promise<void>;
  maxImages: number;
}

const HomepageImageManagement: React.FC<HomepageImageManagementProps> = ({
  images,
  isLoading,
  error,
  onAddImage,
  onUpdateImage,
  onDeleteImage,
  maxImages
}) => {
  const [selectedImage, setSelectedImage] = useState<HomepageImage | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleDelete = async (id: string, imageUrl: string) => {
    try {
      await onDeleteImage(id, imageUrl);
      setAlertMessage('Imagem excluída com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error deleting image:', error);
      setAlertMessage('Erro ao excluir imagem. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  const handleEdit = async (id: string, data: Partial<HomepageImage>) => {
    try {
      await onUpdateImage(id, data);
      setAlertMessage('Imagem atualizada com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error updating image:', error);
      setAlertMessage('Erro ao atualizar imagem. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  const handleAdd = async (file: File, imageData: any) => {
    try {
      await onAddImage(file, imageData);
      setAlertMessage('Imagem adicionada com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error adding image:', error);
      setAlertMessage('Erro ao adicionar imagem. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-800">Erro ao carregar imagens: {error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Imagens da Página Inicial</h2>
          <p className="text-sm text-gray-600">
            {images.length} de {maxImages} imagens utilizadas
          </p>
        </div>

        {images.length >= maxImages ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <p className="text-yellow-800">
              Limite máximo de imagens ({maxImages}) atingido. Exclua algumas imagens antes de adicionar mais.
            </p>
          </div>
        ) : (
          <HomepageImageForm onAddImage={handleAdd} />
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {images.map((image) => (
          <div 
            key={image.id} 
            className="relative group bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <img
              src={image.image_url}
              alt="Homepage decoration"
              className="w-full h-48 object-cover rounded"
            />
            <div className="mt-2 space-y-2">
              <div className="text-sm text-gray-600">
                <div>Position: {image.top_pos}, {image.left_pos}</div>
                <div>Rotation: {image.rotation}</div>
                <div>Size: {image.width_val} x {image.height_val}</div>
                <div>Z-Index: {image.z_index_val}</div>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setSelectedImage(image)}
                  className="p-2 text-wedding-blue-600 hover:bg-wedding-blue-50 rounded-full transition-colors"
                  title="Edit image"
                >
                  <Pencil size={18} />
                </button>
                <button
                  onClick={() => handleDelete(image.id, image.image_url)}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                  title="Delete image"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedImage && (
        <EditHomepageImageModal
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onSubmit={handleEdit}
        />
      )}

      <AlertDialog
        isOpen={showAlert}
        title="Notificação"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </div>
  );
};

export default HomepageImageManagement;